import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FilterSectionComponent } from './components/filter-section/filter-section.component';
import { HeaderHomeComponent } from '../../shared/component/header-home/header-home.component';
import { ListSectionComponent } from './components/list-section/list-section.component';
import { TopSectionComponent } from './components/top-section/top-section.component';
import { JobOfferService, LandingPageService } from 'src/app/api/services';
import { APP_CONSTANTS } from 'src/app/core/constant/constants';
import {
  JobOfferBasicResult,
  Pagination,
} from 'src/app/shared/model/job-offer-result';
import { ToastService } from 'src/app/shared/service/toast.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { BanerPublicityComponent } from 'src/app/shared/component/baner-publicity/baner-publicity.component';

@Component({
  standalone: true,
  selector: 'app-home',
  imports: [
    FilterSectionComponent,
    CommonModule,
    HeaderHomeComponent,
    ListSectionComponent,
    TopSectionComponent,
    BanerPublicityComponent,
  ],
  templateUrl: './home.component.html',
})
export class HomeComponent {
  jobTypes: QueryHeader[] = [];
  jobOffers: JobOffers[] = [];
  province: QueryHeader[] = [];

  jobs: JobOfferBasicResult[] = [];
  isLoading: boolean = false;

  pagination: Pagination = {
    current: 1,
    perPage: 5,
    total: 0,
    lastPage: 0,
  };

  default_page: number = 1;

  currentFilters: any = {};
  headerFilters: any = {};
  sectionFilters: any = {};

  constructor(
    private landingService: LandingPageService,
    private jobOfferService: JobOfferService,
    private toastService: ToastService
  ) {
    this.load();
    this.loadInitialJobs();
  }

  load(): void {
    const payload = {
      idDepartament: APP_CONSTANTS.APURIMAC_ID,
    };

    this.landingService.seccionMenuFiltroIdDepartament10Get(payload).subscribe({
      next: (res) => {
        const dto = res?.dto || {};
        this.province = (dto.provinces?.data || []).map((prov: any) => ({
          label: prov.name,
          value: prov.id,
        }));
        this.jobTypes = (dto.jobTypes?.data || []).map((type: any) => ({
          label: type.description,
          value: type.id,
        }));
        this.jobOffers = (dto.jobOffers?.data || []).map((job: JobOffers) => ({
          id: job.id,
          title: job.title,
          companyName: job.companyName,
          companyUrlBannerMini: job.companyUrlBannerMini,
          publicationDate: job.publicationDate,
          salary: job.salary,
          showSalary: job.showSalary,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.LANDING.GET_DATA_ERROR]
        );
      },
    });
  }

  loadInitialJobs(page: number = 1): void {
    this.isLoading = true;

    const params = {
      skip: (page - 1) * this.pagination.perPage,
      quantity: this.pagination.perPage,
    };

    this.jobOfferService.getAdvancedSearch(params).subscribe({
      next: (res) => {
        this.jobs = res?.dto?.jobOffers.data || [];
        this.pagination = res?.dto?.jobOffers.pagination || this.pagination;
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.SEARCH.GET_RESULTS_ERROR]
        );
      },
      complete: () => (this.isLoading = false),
    });
  }

  onHeaderFiltersChanged(filters: any) {
    this.headerFilters = filters;
    this.callAdvancedSearch(this.default_page);
  }

  onSectionFiltersChanged(filters: any) {
    this.sectionFilters = filters;
    this.callAdvancedSearch(this.default_page);
  }

  callAdvancedSearch(page: number) {
    this.isLoading = true;

    const combinedFilters = {
      ...this.headerFilters,
      ...this.sectionFilters,
      skip: (page - 1) * this.pagination.perPage,
      quantity: this.pagination.perPage,
    };

    this.jobOfferService.getAdvancedSearch(combinedFilters).subscribe({
      next: (res) => {
        this.jobs = res?.dto?.jobOffers.data || [];
        this.pagination = res?.dto?.jobOffers.pagination || this.pagination;
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.SEARCH.GET_RESULTS_ERROR]
        );
      },
      complete: () => (this.isLoading = false),
    });
  }

  goToPage(page: number): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (page >= 1 && page <= this.pagination.lastPage) {
      this.loadInitialJobs(page);
    }
  }

  nextPage(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (this.pagination.current < this.pagination.lastPage) {
      this.goToPage(this.pagination.current + 1);
    }
  }

  prevPage(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (this.pagination.current > 1) {
      this.goToPage(this.pagination.current - 1);
    }
  }

  get showingFrom(): number {
    return (this.pagination.current - 1) * this.pagination.perPage + 1;
  }

  get showingTo(): number {
    return Math.min(
      this.pagination.current * this.pagination.perPage,
      this.pagination.total
    );
  }

  get pagesToShow(): number[] {
    const total = this.pagination.lastPage;
    const current = this.pagination.current;
    const delta = 1;

    const pages: number[] = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    pages.push(1);

    if (left > 2) {
      pages.push(-1);
    }
    for (let i = left; i <= right; i++) {
      pages.push(i);
    }
    if (right < total - 1) {
      pages.push(-1);
    }
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  }
}
